<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      permanent
      :rail="rail"
      @click="rail = false"
    >
      <!-- User Info Section -->
      <v-list>
        <v-list-item
          :prepend-avatar="userAvatar"
          :title="userStore.userFullName"
          :subtitle="userStore.userPosition"
          class="user-info"
        >
          <template v-slot:append>
            <v-btn
              :icon="rail ? 'mdi-chevron-right' : 'mdi-chevron-left'"
              variant="text"
              @click.stop="rail = !rail"
            ></v-btn>
          </template>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <!-- Enterprise Selection -->
      <v-list v-if="!rail" class="pa-2">
        <v-select
          v-model="userStore.selectedEnterprise"
          :items="userStore.enterprises"
          item-title="name"
          item-value="id"
          label="Предприятие"
          variant="outlined"
          density="compact"
          prepend-inner-icon="mdi-office-building"
          :loading="userStore.loading"
          @update:model-value="handleEnterpriseChange"
        ></v-select>
      </v-list>

      <v-divider></v-divider>

      <!-- Main Navigation Menu -->
      <v-list nav density="compact">
        <!-- Рабочий стол -->
        <v-list-item
          prepend-icon="mdi-view-dashboard"
          title="Рабочий стол"
          value="dashboard"
          :to="{ name: 'dashboard' }"
          exact
        ></v-list-item>

        <!-- Журналы (скрыто) -->
        <!--
        <v-list-item
          prepend-icon="mdi-book-open-variant"
          title="Журналы"
          value="journals"
          :to="{ name: 'journals' }"
        ></v-list-item>
        -->

        <!-- Чек-листы -->
        <v-list-item
          prepend-icon="mdi-clipboard-check-multiple"
          title="Чек-листы"
          value="checklists"
          :to="{ name: 'checklists' }"
        ></v-list-item>

        <!-- Календарь -->
        <v-list-item
          prepend-icon="mdi-calendar"
          title="Календарь"
          value="calendar"
          :to="{ name: 'calendar' }"
        ></v-list-item>

        <!-- Справочники (with submenu) - показываем только если есть доступные -->
        <v-list-group v-if="availableHandbooks.length > 0" value="handbooks">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-book-multiple"
              title="Справочники"
            ></v-list-item>
          </template>

          <!-- Должности -->
          <v-list-item
            v-if="hasHandbookAccess('POSITION')"
            prepend-icon="mdi-briefcase"
            title="Должности"
            value="positions"
            :to="{ name: 'positions' }"
          ></v-list-item>

          <!-- Сотрудники -->
          <v-list-item
            v-if="hasHandbookAccess('STAFFER')"
            prepend-icon="mdi-account-group"
            title="Сотрудники"
            value="employees"
            :to="{ name: 'employees' }"
          ></v-list-item>

          <!-- Цеха (помещения) -->
          <v-list-item
            v-if="hasHandbookAccess('DEPARTMENT')"
            prepend-icon="mdi-warehouse"
            title="Цеха (помещения)"
            value="workshops"
            :to="{ name: 'workshops' }"
          ></v-list-item>

          <!-- Оборудование -->
          <v-list-item
            v-if="hasHandbookAccess('HARDWARE')"
            prepend-icon="mdi-tools"
            title="Оборудование"
            value="equipment"
            :to="{ name: 'equipment' }"
          ></v-list-item>

          <!-- Сырье -->
          <v-list-item
            v-if="hasHandbookAccess('RAW_STOCK')"
            prepend-icon="mdi-basket"
            title="Сырье"
            value="rawMaterials"
            :to="{ name: 'rawMaterials' }"
          ></v-list-item>

          <!-- Продукция готовая -->
          <v-list-item
            v-if="hasHandbookAccess('END_PRODUCT')"
            prepend-icon="mdi-package-variant"
            title="Продукция готовая"
            value="products"
            :to="{ name: 'products' }"
          ></v-list-item>

          <!-- Производитель/поставщик -->
          <v-list-item
            v-if="hasHandbookAccess('PROVIDER')"
            prepend-icon="mdi-truck"
            title="Производитель/поставщик"
            value="suppliers"
            :to="{ name: 'suppliers' }"
          ></v-list-item>

          <!-- График работы сотрудников -->
          <v-list-item
            v-if="hasHandbookAccess('WORK_SCHEDULE')"
            prepend-icon="mdi-calendar-clock"
            title="График работы сотрудников"
            value="workSchedule"
            :to="{ name: 'workSchedule' }"
          ></v-list-item>
        </v-list-group>
      </v-list>

      <!-- Bottom Navigation (Settings & Logout) -->
      <template v-slot:append>
        <v-list nav density="compact">
          <v-divider></v-divider>
          
          <v-list-item
            prepend-icon="mdi-cog"
            title="Настройки"
            value="settings"
            :to="{ name: 'settings' }"
          ></v-list-item>

          <v-list-item
            prepend-icon="mdi-logout"
            title="Выход"
            value="logout"
            @click="handleLogout"
          ></v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Main Content Area -->
    <v-main>
      <v-container fluid>
        <router-view></router-view>
      </v-container>
    </v-main>

    <!-- Loading Overlay -->
    <v-overlay
      v-model="userStore.loading"
      class="align-center justify-center"
      persistent
    >
      <v-progress-circular
        color="primary"
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showError"
      color="error"
      :timeout="5000"
      location="top"
    >
      {{ userStore.error }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showError = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useUserStore } from '@/stores/user.store'

const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()

const drawer = ref(true)
const rail = ref(false)
const showError = ref(false)

// Watch for errors
watch(
  () => userStore.error,
  (newError) => {
    if (newError) {
      showError.value = true
    }
  }
)

// Computed avatar
const userAvatar = computed(() => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(userStore.userFullName)}&background=1976D2&color=fff`
})

// Check if user has access to handbook
const hasHandbookAccess = (handbookType) => {
  // Superuser has access to everything
  if (userStore.user.isSuperuser) return true
  
  // Check handbook permissions
  const permissions = userStore.permissions.handbook_permissions?.[handbookType]
  if (!permissions) return false
  
  // User has access if they have at least READ permission
  return permissions.READ_HANDBOOK === true
}

// Get list of available handbooks
const availableHandbooks = computed(() => {
  const handbooks = []
  const handbookTypes = ['POSITION', 'STAFFER', 'DEPARTMENT', 'HARDWARE', 'RAW_STOCK', 'END_PRODUCT', 'PROVIDER', 'WORK_SCHEDULE']
  
  handbookTypes.forEach(type => {
    if (hasHandbookAccess(type)) {
      handbooks.push(type)
    }
  })
  
  return handbooks
})

// Handle Enterprise Change
const handleEnterpriseChange = async (enterpriseId) => {
  try {
    await userStore.selectEnterprise(enterpriseId)
  } catch (error) {
    console.error('Error changing enterprise:', error)
  }
}

// Handle Logout
const handleLogout = () => {
  authStore.logout()
  router.push({ name: 'login' })
}
</script>

<style scoped>
.user-info {
  padding: 16px;
}

:deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
}
</style>
